// In-grid editing for the book grid (landing page). Pure: which field a cell
// edits, the PATCH body, the undo body, and the optimistic row update. The
// server stays authoritative (PATCH /api/clients/:id/compliance recomputes).
import { DEFAULT_WINDOWS, evaluateCompliance, type ComplianceWindows } from './compliance.js';
import type { ComplianceRow, DashboardRow } from './types.js';

type RuleKey = 'riskProfile' | 'factFind' | 'snapshot' | 'bestInterest' | 'soaSigned' | 'feeConsent';

export type EditTarget =
  /** A dated compliance check: record the date it was last done. */
  | { kind: 'ruleDate'; column: string; label: string; rule: RuleKey; field: string; windowDays: number }
  /** A flag check (Update Needed / Write SoA / Write RoA): set or clear. */
  | { kind: 'flag'; column: string; label: string; check: 'updateNeeded' | 'writeSoa' | 'writeRoa'; field: 'updatePending' | 'soaNeeded' | 'roaNeeded' }
  /** File Note: satisfied by actually writing a note. */
  | { kind: 'note'; column: string; label: string }
  /** A plain date column (Call Due / Review Date). */
  | { kind: 'date'; column: string; label: string; field: 'callDue' | 'reviewDue' };

const RULE_FIELDS: Record<RuleKey, { field: string; label: string }> = {
  riskProfile: { field: 'lastRiskProfileDate', label: 'Risk Profile' },
  factFind: { field: 'lastFactFindDate', label: 'Fact Find' },
  snapshot: { field: 'lastSnapshotDate', label: 'Snapshot' },
  bestInterest: { field: 'lastBestInterestDate', label: 'Best Interest' },
  soaSigned: { field: 'soaSignedDate', label: 'SoA Signed' },
  feeConsent: { field: 'lastFeeConsentDate', label: 'Fee Consent' },
};

/** The edit a grid column supports, or null when the column is read-only. */
export function editTargetFor(columnKey: string, windows: ComplianceWindows = DEFAULT_WINDOWS): EditTarget | null {
  if (columnKey === 'callDue') return { kind: 'date', column: columnKey, label: 'Call Due', field: 'callDue' };
  if (columnKey === 'reviewDue') return { kind: 'date', column: columnKey, label: 'Review Date', field: 'reviewDue' };
  if (!columnKey.startsWith('c.')) return null;
  const check = columnKey.slice(2) as keyof ComplianceRow;
  if (check === 'fileNote') return { kind: 'note', column: columnKey, label: 'File Note' };
  if (check === 'updateNeeded') return { kind: 'flag', column: columnKey, label: 'Update Needed', check, field: 'updatePending' };
  if (check === 'writeSoa') return { kind: 'flag', column: columnKey, label: 'Write SoA', check, field: 'soaNeeded' };
  if (check === 'writeRoa') return { kind: 'flag', column: columnKey, label: 'Write RoA', check, field: 'roaNeeded' };
  if (check in RULE_FIELDS) {
    const rule = check as RuleKey;
    return { kind: 'ruleDate', column: columnKey, label: RULE_FIELDS[rule].label, rule, field: RULE_FIELDS[rule].field, windowDays: windows[rule] };
  }
  return null;
}

/** "5 years" / "6 months" / "90 days", for the editor's rule hint. */
export function windowLabel(days: number): string {
  if (days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  if (days >= 180 && days <= 183) return '6 months';
  return `${days} days`;
}

/** The value the cell edits, as currently held on the row (for display + undo). */
export function currentValue(row: DashboardRow, t: EditTarget): string | boolean | null {
  switch (t.kind) {
    case 'ruleDate': return row.complianceDates?.[t.rule] ?? null;
    case 'flag': return row.compliance[t.check] === 'outstanding';
    case 'date': return row[t.field] ?? null;
    case 'note': return row.lastFileNote ?? null;
  }
}

/** PATCH /compliance body for a new value. Not used for notes (they POST a note). */
export function patchBody(t: EditTarget, value: string | boolean | null): Record<string, string | boolean | null> {
  if (t.kind === 'note') throw new Error('notes are created, not patched');
  return { [t.field]: value };
}

/** Add calendar months to a yyyy-mm-dd date, clamping to month end (31 Jan + 1m = 28/29 Feb). */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const total = (y * 12 + (m - 1)) + months;
  const ny = Math.floor(total / 12);
  const nm = total % 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return `${ny}-${String(nm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/**
 * The row as it will look after an edit, with compliance recomputed by the
 * same rules the server uses, so the cell flips colour instantly. The server
 * response replaces it moments later.
 */
export function applyEdit(row: DashboardRow, t: EditTarget, value: string | boolean | null, now: Date = new Date(), windows: ComplianceWindows = DEFAULT_WINDOWS): DashboardRow {
  const dates = { ...(row.complianceDates ?? {}) };
  let lastFileNote = row.lastFileNote;
  let callDue = row.callDue;
  let reviewDue = row.reviewDue;
  let feeConsentDate = row.feeConsentDate;
  const flags = {
    updatePending: row.compliance.updateNeeded === 'outstanding',
    soaNeeded: row.compliance.writeSoa === 'outstanding',
    roaNeeded: row.compliance.writeRoa === 'outstanding',
  };
  if (t.kind === 'ruleDate') {
    dates[t.rule] = (value as string | null) ?? null;
    if (t.rule === 'feeConsent') feeConsentDate = (value as string | null) ?? null;
  } else if (t.kind === 'flag') flags[t.field] = Boolean(value);
  else if (t.kind === 'note') lastFileNote = (value as string | null) ?? lastFileNote;
  else if (t.field === 'callDue') callDue = (value as string | null) ?? null;
  else reviewDue = (value as string | null) ?? null;

  const compliance = evaluateCompliance({
    lastRiskProfile: dates.riskProfile ?? null,
    lastFactFind: dates.factFind ?? null,
    lastSnapshot: dates.snapshot ?? null,
    lastFileNote: lastFileNote ?? null,
    lastBestInterest: dates.bestInterest ?? null,
    soaSignedDate: dates.soaSigned ?? null,
    lastFeeConsent: dates.feeConsent ?? null,
    ...flags,
    now,
  }, windows);
  // Checks the row doesn't carry dates for keep their server state.
  if (!row.complianceDates) {
    for (const k of Object.keys(RULE_FIELDS) as RuleKey[]) {
      if (!(t.kind === 'ruleDate' && t.rule === k)) compliance[k] = row.compliance[k];
    }
  }
  return { ...row, compliance, complianceDates: dates, lastFileNote, callDue, reviewDue, feeConsentDate };
}
